import type { Timestamp } from 'firebase/firestore';
import type { BaseFirestoreEntity } from '../../../types/commonTypes';

// ---------------------------------------------------------------------------
// Enums / union types
// ---------------------------------------------------------------------------

export const DayKind = {
  COURAGE: 'courage',
  PRACTICE: 'practice',
  SURVIVAL: 'survival',
} as const;
export type DayKind = (typeof DayKind)[keyof typeof DayKind];

export const DAY_KIND_LABELS: Record<DayKind, string> = {
  [DayKind.COURAGE]: 'Courage',
  [DayKind.PRACTICE]: 'Practice',
  [DayKind.SURVIVAL]: 'Survival',
};

export const DailyTaskCategory = {
  ERP_PRACTICE: 'erp_practice',
  MINDFULNESS: 'mindfulness',
  JOURNALING: 'journaling',
  PHYSICAL_WELLNESS: 'physical_wellness',
  SOCIAL_CONNECTION: 'social_connection',
  APP_ENGAGEMENT: 'app_engagement',
  HUMOR_SELF_COMPASSION: 'humor_self_compassion',
  TRIGGER_SPECIFIC: 'trigger_specific',
} as const;
export type DailyTaskCategory = (typeof DailyTaskCategory)[keyof typeof DailyTaskCategory];

export const DAILY_TASK_CATEGORY_LABELS: Record<DailyTaskCategory, string> = {
  [DailyTaskCategory.ERP_PRACTICE]: 'ERP Practice',
  [DailyTaskCategory.MINDFULNESS]: 'Mindfulness',
  [DailyTaskCategory.JOURNALING]: 'Journaling',
  [DailyTaskCategory.PHYSICAL_WELLNESS]: 'Physical Wellness',
  [DailyTaskCategory.SOCIAL_CONNECTION]: 'Social Connection',
  [DailyTaskCategory.APP_ENGAGEMENT]: 'App Engagement',
  [DailyTaskCategory.HUMOR_SELF_COMPASSION]: 'Humor / Self-Compassion',
  [DailyTaskCategory.TRIGGER_SPECIFIC]: 'Trigger Specific',
};

export const DAILY_TASK_CATEGORY_ORDER: DailyTaskCategory[] = [
  DailyTaskCategory.ERP_PRACTICE,
  DailyTaskCategory.MINDFULNESS,
  DailyTaskCategory.JOURNALING,
  DailyTaskCategory.PHYSICAL_WELLNESS,
  DailyTaskCategory.SOCIAL_CONNECTION,
  DailyTaskCategory.APP_ENGAGEMENT,
  DailyTaskCategory.HUMOR_SELF_COMPASSION,
  DailyTaskCategory.TRIGGER_SPECIFIC,
];

export const DailyTaskActionType = {
  MANUAL: 'manual',
  DEEP_LINK: 'deep_link',
} as const;
export type DailyTaskActionType = (typeof DailyTaskActionType)[keyof typeof DailyTaskActionType];

export const DAILY_TASK_ACTION_TYPE_LABELS: Record<DailyTaskActionType, string> = {
  [DailyTaskActionType.MANUAL]: 'Manual (self-report)',
  [DailyTaskActionType.DEEP_LINK]: 'Deep Link (in-app)',
};

export const DailyTaskDeepLinkTarget = {
  TRIGGER_LOG: 'trigger_log',
} as const;
export type DailyTaskDeepLinkTarget = (typeof DailyTaskDeepLinkTarget)[keyof typeof DailyTaskDeepLinkTarget];

export const DAILY_TASK_DEEP_LINK_TARGET_LABELS: Record<DailyTaskDeepLinkTarget, string> = {
  [DailyTaskDeepLinkTarget.TRIGGER_LOG]: 'Trigger Log',
};

export const DailyTaskColorVariant = {
  YELLOW: 'yellow',
  BLUE: 'blue',
  GREEN: 'green',
  PINK: 'pink',
} as const;
export type DailyTaskColorVariant = (typeof DailyTaskColorVariant)[keyof typeof DailyTaskColorVariant];

export const DAILY_TASK_COLOR_VARIANT_LABELS: Record<DailyTaskColorVariant, string> = {
  [DailyTaskColorVariant.YELLOW]: 'Yellow',
  [DailyTaskColorVariant.BLUE]: 'Blue',
  [DailyTaskColorVariant.GREEN]: 'Green',
  [DailyTaskColorVariant.PINK]: 'Pink',
};

// ---------------------------------------------------------------------------
// Template interface
// ---------------------------------------------------------------------------

export interface DailyTaskTemplateFirestore extends BaseFirestoreEntity {
  id?: string;
  title: string;
  emoji: string;
  isPublished: boolean;

  colorVariant: DailyTaskColorVariant;

  category: DailyTaskCategory;
  actionType: DailyTaskActionType;
  deepLinkTarget?: DailyTaskDeepLinkTarget;
  deepLinkParams?: Record<string, string>;
  totalSteps: number;
  energy: number;

  textTemplatingPrompt: string;

  ocdSubtypeTags: string[];
  journeyChapterRelevance: number[];
  dayKindTags: DayKind[];
}

// ---------------------------------------------------------------------------
// Generated daily task (per-user instance, stored at user_info/{uid}.dailyTasks)
// ---------------------------------------------------------------------------

export const DailyTaskStatus = {
  AVAILABLE: 'available',
  COMPLETED: 'completed',
  CLAIMED: 'claimed',
} as const;
export type DailyTaskStatus = (typeof DailyTaskStatus)[keyof typeof DailyTaskStatus];

export const DAILY_TASK_STATUS_OPTIONS: DailyTaskStatus[] = [
  DailyTaskStatus.AVAILABLE,
  DailyTaskStatus.COMPLETED,
  DailyTaskStatus.CLAIMED,
];

export const DailyTaskSource = {
  AI_GENERATED: 'ai_generated',
  USER_ADDED: 'user_added',
} as const;
export type DailyTaskSource = (typeof DailyTaskSource)[keyof typeof DailyTaskSource];

export interface GeneratedDailyTask {
  id: string;
  templateId: string;
  title: string;
  description: string;
  emoji: string;
  category: DailyTaskCategory;
  colorVariant: DailyTaskColorVariant;
  actionType: DailyTaskActionType;
  deepLinkTarget?: DailyTaskDeepLinkTarget;
  deepLinkParams?: Record<string, string>;
  totalSteps: number;
  completedSteps: number;
  energy: number;
  status: DailyTaskStatus;
  source: DailyTaskSource;
  order: number;
}

export interface DailyTasksState {
  generatedForDate: string;
  generatedAt?: Timestamp;
  dayKind: DayKind;
  tasks: Record<string, GeneratedDailyTask>;
}
